import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Points from 'components/Points';
import { NoPrice } from 'components/NoPrice/NoPrice';
import { FoilCardImage } from 'components/FoilCardImage';
import WantListCardOverlay from 'screens/Public/UserProfile/components/ProfileWantListTab/WantListCardOverlay';
import { CardFinish, HaveFinish, finishFromRow } from 'utils/finishes';
import { getCardImageUrl, makeCardImageErrorHandler } from 'utils/cardImageFallback';
import { buildCardUrl } from 'utils/cardUrlUtils';
import './OfferGridTile.css';

export interface OfferGridTileCard {
  name: string;
  imageUrl: string | null;
  scryfallId: string | null;
  setCode?: string | null;
  collectorNumber?: string | null;
}

export interface OfferGridTileProps {
  /** Stable key for the image fallback chain (have row id or want entry id). */
  imageKey: string | number;
  card: OfferGridTileCard;
  /** The ROW's finish (never inferred from the image); drives the sheen and the badge. */
  foil?: boolean;
  finish?: HaveFinish | null;
  /** Want entries: the finish the entry's preference renders as, already resolved. */
  artFinish?: CardFinish;
  /** The set line: "USG #60 · NM · English", or a want entry's edition/condition summary. */
  meta: React.ReactNode;
  /** Quoted/display price; null renders NoPrice. Never estimated. */
  pricePoints: number | null | undefined;
  /** Owned or wanted quantity; renders as a badge on the art when > 1. */
  quantityBadge?: number;
  /** Receive side: this have row matches one of the viewer's want entries (Decision 5 badge). */
  onYourList?: boolean;
  /** Send full list: the viewer owns this entry (gold "In your collection" treatment). */
  owned?: boolean;
  /** Small line under the name, e.g. the want budget "they want 2 · 1 left". */
  caption?: React.ReactNode;
  /** The overlay's Add to Trade (QA round 1, Decision 4). */
  onAdd: () => void;
  /** This tile's line is in the cart: the overlay shows its "✓ Added" state. */
  added?: boolean;
  /** Clicking "✓ Added" removes the line. Omitted, the Added state is static. */
  onRemove?: () => void;
  /** Disables Add to Trade; shown as the button's tooltip. */
  addDisabledReason?: string;
  /** A visible line under the action (the accept-condition reason). */
  addDisabledNote?: string;
  /** Points the Add to Trade arrow at the cart this tile fills: 'left' on the receive grid. */
  addArrow?: 'right' | 'left';
  /** Phone: 44px tap targets. */
  phone?: boolean;
}

/**
 * One tile of the focus-mode grid (Asana 1219788320590626 Decision 5, revised
 * by 1219902279124885 Decision 4): art carrying FoilCardImage's finish chip
 * bottom-left and ON YOUR LIST top-right, then name, set line and the branded
 * price. Hovering (desktop) or a first tap (phone) covers the art with the
 * profile have list's WantListCardOverlay, which owns Add to Trade, ✓ Added
 * and the card-page link; the badges hide while it shows, as on the profile.
 * Image fallback state lives in a ref, never DOM dataset (CLAUDE.md #15).
 */
export const OfferGridTile: React.FC<OfferGridTileProps> = ({
  imageKey,
  card,
  foil = false,
  finish,
  artFinish,
  meta,
  pricePoints,
  quantityBadge,
  onYourList = false,
  owned = false,
  caption,
  onAdd,
  added = false,
  onRemove,
  addDisabledReason,
  addDisabledNote,
  addArrow = 'right',
  phone = false,
}) => {
  const navigate = useNavigate();
  const fallbackStepRef = useRef<Map<string | number, number>>(new Map());
  const imageInputs = { image: card.imageUrl, scryfallId: card.scryfallId };
  const resolvedFinish: CardFinish = artFinish ?? finishFromRow({ finish, foil });

  // Hover, tap and close state as ProfileHaveListTab's HaveListCardItem: a
  // hover shows the overlay on desktop; on a phone the first tap reveals it
  // (with its close button) and the second action adds. The tile body itself
  // never navigates: the overlay's arrow and title are the card-page link, and
  // a stray click must not drop the page-held focus state and loaded pages.
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileTapped, setIsMobileTapped] = useState(false);
  const showOverlay = isHovered || isMobileTapped;

  const handleClick = (e: React.MouseEvent) => {
    if (window.innerWidth <= 768 && !isMobileTapped) {
      e.preventDefault();
      e.stopPropagation();
      setIsMobileTapped(true);
    }
  };
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsMobileTapped(false);
  };
  const handleOverlayClose = () => {
    setIsMobileTapped(false);
    setIsHovered(false);
  };
  const handleViewCard = () => {
    navigate(
      buildCardUrl({
        name: card.name,
        setCode: card.setCode,
        collectorNumber: card.collectorNumber,
        scryfallId: card.scryfallId ?? undefined,
      })
    );
  };

  return (
    <div
      className={`offer-grid-tile${owned ? ' offer-grid-tile--owned' : ''}${
        phone ? ' offer-grid-tile--phone' : ''
      }`}
      data-testid="offer-grid-tile"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="offer-grid-tile-art">
        {/* FoilCardImage renders THE finish chip (the same one the profile
            shows); the tile adds none of its own. */}
        <FoilCardImage
          className="offer-grid-tile-foilwrap"
          src={getCardImageUrl(imageInputs)}
          alt={card.name}
          finish={resolvedFinish}
          loading="lazy"
          onError={makeCardImageErrorHandler(imageInputs, fallbackStepRef, imageKey)}
        />
        {quantityBadge != null && quantityBadge > 1 && !showOverlay && (
          <div className="offer-grid-tile-qty">x{quantityBadge}</div>
        )}
        {onYourList && !showOverlay && (
          <div className="offer-grid-tile-onlist" data-testid="offer-grid-tile-onlist">
            On your list
          </div>
        )}
        {showOverlay && (
          // quantity 1 / addedCount 0|1: "Added" means this line is in the cart
          // (the context rejects a duplicate id), and the overlay's "N left"
          // badge never applies to a tile; the want budget is the caption.
          <WantListCardOverlay
            cardName={card.name}
            quantity={1}
            addedCount={added ? 1 : 0}
            onAddToTrade={onAdd}
            onRemove={added ? onRemove : undefined}
            onViewCard={handleViewCard}
            onClose={isMobileTapped ? handleOverlayClose : undefined}
            isMobile={isMobileTapped}
            mode="trade"
            addDisabledReason={addDisabledReason}
            addDisabledNote={addDisabledNote}
            addArrow={addArrow}
          />
        )}
      </div>
      {owned && <span className="offer-grid-tile-owned-chip">In your collection</span>}
      <div className="offer-grid-tile-name" title={card.name}>
        {card.name}
      </div>
      <div className="offer-grid-tile-meta">{meta}</div>
      {caption && <div className="offer-grid-tile-caption">{caption}</div>}
      <div className="offer-grid-tile-foot">
        <div className="offer-grid-tile-price">
          {pricePoints != null ? <Points type="small" points={pricePoints} /> : <NoPrice />}
        </div>
      </div>
    </div>
  );
};

export default OfferGridTile;
